// services/reminders.api.ts
// Cliente para endpoints del flujo manual de recordatorios (pivot migración 011).

import { authFetch } from './api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ========== Tipos ==========
export interface MessageTemplate {
  id: number;
  professional_id: number;
  template_key: string;
  title: string;
  body: string;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export type ReminderManualStatus =
  | 'sent'
  | 'confirmed'
  | 'no_response'
  | 'cancelled';

export interface ReminderItem {
  id: number;
  date: string;             // YYYY-MM-DD
  time: string;             // HH:MM
  duration: number | null;
  status: string;           // estado del turno (pending/confirmed/...)
  client_name: string | null;
  client_phone: string | null;
  client_email: string | null;
  service_name: string | null;
  reminder_sent: boolean;
  confirmed_at: string | null;
  manual_last_status: ReminderManualStatus | null;
  manual_last_template: string | null;
  manual_last_at: string | null;
}

export interface PendingRemindersResponse {
  success: boolean;
  forTomorrow: ReminderItem[];
  forToday: ReminderItem[];
  dates: { today: string; tomorrow: string };
}

export interface RenderResponse {
  success: boolean;
  template_key: string;
  template_title: string;
  rendered: string;
  waUrl: string | null;
  phone: string | null;
}

// ========== Templates CRUD ==========

export const getTemplates = async (professionalId: number): Promise<MessageTemplate[]> => {
  const res = await authFetch(`${API_URL}/api/message-templates/${professionalId}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Error cargando plantillas');
  return data.templates as MessageTemplate[];
};

export const createTemplate = async (
  professionalId: number,
  payload: { template_key: string; title: string; body: string; is_default?: boolean }
): Promise<MessageTemplate> => {
  const res = await authFetch(`${API_URL}/api/message-templates/${professionalId}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Error creando plantilla');
  return data.template as MessageTemplate;
};

export const updateTemplate = async (
  professionalId: number,
  id: number,
  payload: { title?: string; body?: string; is_default?: boolean }
): Promise<MessageTemplate> => {
  const res = await authFetch(`${API_URL}/api/message-templates/${professionalId}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Error actualizando plantilla');
  return data.template as MessageTemplate;
};

export const deleteTemplate = async (professionalId: number, id: number): Promise<void> => {
  const res = await authFetch(`${API_URL}/api/message-templates/${professionalId}/${id}`, {
    method: 'DELETE',
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Error eliminando plantilla');
};

// ========== Reminders ==========

export const getPendingReminders = async (
  professionalId: number
): Promise<PendingRemindersResponse> => {
  const res = await authFetch(
    `${API_URL}/api/reminders/pending?professionalId=${professionalId}`
  );
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Error cargando recordatorios');
  return data as PendingRemindersResponse;
};

export const renderReminder = async (
  appointmentId: number,
  templateKey?: string
): Promise<RenderResponse> => {
  const qs = templateKey ? `?templateKey=${encodeURIComponent(templateKey)}` : '';
  const res = await authFetch(`${API_URL}/api/reminders/${appointmentId}/render${qs}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Error renderizando mensaje');
  return data as RenderResponse;
};

export const markReminder = async (
  appointmentId: number,
  payload: {
    status: ReminderManualStatus;
    templateKey?: string;
    message_preview?: string;
  }
): Promise<void> => {
  const res = await authFetch(`${API_URL}/api/reminders/${appointmentId}/mark`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Error marcando estado');
};
